import type { SearchBoxEntry, SearchBoxPosition } from "./SearchBox";

/**
 * Point choisi dans un champ de `RoutePanel` — soit une forme (l'entrée réutilise `SearchBoxEntry`,
 * voir son commentaire : `shapeIds[0]` sert de forme "canonique" quand une entité est découpée en
 * plusieurs formes, il n'y a qu'UN point de départ/arrivée possible pour un itinéraire), soit la
 * position utilisateur courante (champ "Départ" uniquement — voir `RoutePanelOptions.hasUserLocation`).
 */
export type RoutePanelSelection =
  | { type: "shape"; shapeId: string }
  | { type: "user-location" }
  /**
   * Destination hors du document affiché (voir `SearchBoxEntry.external`) — aucune forme chargée
   * derrière elle, donc rien que `RoutePanel` ni `core/pathfinding` puissent en faire seuls : c'est
   * le consommateur qui la résout, via `RoutePanelConfig.onValidate`.
   */
  | { type: "external"; payload: unknown };

export interface RoutePanelOptions {
  /** Même mécanisme que `SearchBoxOptions.getEntries` — appelée à chaque frappe, jamais mise en
   * cache, pour rester cohérente avec l'état courant du `ShapeStore`. */
  getEntries: () => SearchBoxEntry[];
  /** Vrai si la position utilisateur courante peut servir de point de départ (voir
   * `MapAdapter.getGeolocationEnabled`/`getUserLocation`) — vérifiée à chaque ouverture ET à chaque
   * frappe dans le champ Départ, jamais mise en cache : peut changer pendant que le panneau est
   * ouvert (désactivation de la géolocalisation en cours de route). */
  hasUserLocation: () => boolean;
  /** Libellé de l'entrée spéciale "position utilisateur" du champ Départ. Par défaut : "Ma position". */
  userLocationLabel?: string;
  /** Coin de l'écran où placer le panneau. Par défaut : "top-right" (`SearchBox`/`ResetViewButton`
   * occupent déjà "top-left" par défaut). */
  position?: SearchBoxPosition;
  /** Fusionné par-dessus le style par défaut — même convention que `ResetViewButtonOptions.style`. */
  style?: Partial<CSSStyleDeclaration>;
  fromPlaceholder?: string;
  toPlaceholder?: string;
  /** Libellé affiché au-dessus du champ Départ. Par défaut : "Départ". */
  fromLabel?: string;
  /** Libellé affiché au-dessus du champ Arrivée. Par défaut : "Arrivée". */
  toLabel?: string;
  validateLabel?: string;
}

const POSITION_OFFSETS: Record<SearchBoxPosition, Partial<CSSStyleDeclaration>> = {
  "top-left": { top: "10px", left: "10px" },
  "top-right": { top: "10px", right: "10px" },
  "bottom-left": { bottom: "10px", left: "10px" },
  "bottom-right": { bottom: "10px", right: "10px" },
};

/** Insensible à la casse ET aux accents — même fonction que `ui/SearchBox.ts` (dupliquée plutôt que
 * partagée : trop petite pour justifier un module utilitaire séparé entre les deux). */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

const MAX_RESULTS = 20;

/** Une ligne de résultat d'un champ `RoutePanel` — soit une forme (`entry`), soit l'entrée spéciale
 * "position utilisateur" (`isUserLocation`), jamais les deux. */
interface FieldRow {
  isUserLocation: boolean;
  entry: SearchBoxEntry | null;
}

/**
 * Un champ "Départ"/"Arrivée" : input texte + liste déroulante filtrée, même interaction que
 * `SearchBox` (frappe pour filtrer, clic pour choisir, bouton "x" pour effacer) mais SANS son
 * positionnement en coin d'écran (`RoutePanel` place les deux champs lui-même dans son propre
 * panneau) — d'où une classe dédiée plutôt qu'une réutilisation de `SearchBox`.
 */
class RouteField {
  readonly wrapper: HTMLDivElement;
  private input: HTMLInputElement;
  private clearButton: HTMLButtonElement;
  private resultsEl: HTMLDivElement;
  private selection: RoutePanelSelection | null = null;
  /** Résultats actuellement affichés dans le menu déroulant — voir `handleKeyDown` (Entrée
   * sélectionne le premier de cette liste, jamais recalculée séparément). */
  private currentRows: FieldRow[] = [];
  /** Index du résultat actuellement surligné (voir `setActiveIndex`) — même convention que
   * `SearchBox.activeIndex` : `-1` quand rien n'est affiché/surligné. */
  private activeIndex = -1;

  constructor(
    private getRows: (query: string) => FieldRow[],
    label: string,
    placeholder: string,
    private userLocationLabel: string,
    private onChange: () => void,
    /** Appelée sur Entrée dans le champ, APRÈS sélection éventuelle du premier résultat affiché
     * (voir `handleKeyDown`) — permet à `RoutePanel` de tenter une validation immédiate si les
     * deux champs ont désormais une sélection, sans dupliquer ici sa logique de bouton Valider. */
    private onEnter: () => void,
  ) {
    this.wrapper = document.createElement("div");
    this.wrapper.className = "toka-route-panel__field";

    const labelEl = document.createElement("label");
    labelEl.className = "toka-route-panel__label";
    labelEl.textContent = label;
    this.wrapper.appendChild(labelEl);

    const inputWrapper = document.createElement("div");
    inputWrapper.className = "toka-route-panel__input-wrapper";

    this.input = document.createElement("input");
    this.input.type = "text";
    this.input.className = "toka-route-panel__input";
    this.input.placeholder = placeholder;
    this.input.setAttribute("aria-label", label);
    this.input.addEventListener("input", this.handleInput);
    this.input.addEventListener("focus", this.handleInput);
    this.input.addEventListener("keydown", this.handleKeyDown);
    inputWrapper.appendChild(this.input);

    this.clearButton = document.createElement("button");
    this.clearButton.type = "button";
    this.clearButton.className = "toka-route-panel__clear";
    this.clearButton.textContent = "✕";
    this.clearButton.setAttribute("aria-label", "Effacer");
    // `mousedown` (pas `click`) : même raison que les lignes de résultat — se déclenche avant le
    // `blur` de l'input qui masquerait sinon ce bouton (`updateClearButtonVisibility` posé sur
    // `blur` n'existe pas ici, mais `handleOutsideClick` sur `mousedown` document-level fermerait
    // la liste avant que le clic n'atteigne le bouton s'il utilisait `click`).
    this.clearButton.addEventListener("mousedown", (event) => {
      event.preventDefault();
      this.clear();
    });
    inputWrapper.appendChild(this.clearButton);
    this.updateClearButtonVisibility();

    this.wrapper.appendChild(inputWrapper);

    this.resultsEl = document.createElement("div");
    this.resultsEl.className = "toka-route-panel__results";
    this.wrapper.appendChild(this.resultsEl);

    document.addEventListener("mousedown", this.handleOutsideClick);
  }

  getSelection(): RoutePanelSelection | null {
    return this.selection;
  }

  /** Pose une sélection depuis l'extérieur (préremplissage — voir `RoutePanel.show`) — `null` vide
   * le champ. Ignoré silencieusement si l'entrée demandée n'existe plus (`getRows` ne la retourne
   * pas), même convention que `MapAdapter.highlightShapes` pour un id inconnu. */
  setSelection(selection: RoutePanelSelection | null): void {
    if (!selection) {
      this.selection = null;
      this.input.value = "";
      this.updateClearButtonVisibility();
      return;
    }
    if (selection.type === "user-location") {
      this.selection = selection;
      this.input.value = this.userLocationLabel;
      this.updateClearButtonVisibility();
      return;
    }
    // Une destination hors document ne se préremplit pas : les seuls appelants partent d'une forme
    // (voir `StartRouteModeOptions.prefillFromShapeId`/`prefillToShapeId`), donc du document affiché.
    if (selection.type === "external") return;
    const row = this.getRows("").find((r) => r.entry?.shapeIds.includes(selection.shapeId));
    if (!row?.entry) return;
    this.selection = selection;
    this.input.value = row.entry.title;
    this.updateClearButtonVisibility();
  }

  private handleInput = (): void => {
    this.updateClearButtonVisibility();
    const query = normalize(this.input.value.trim());
    const rows = this.getRows(query)
      .filter((row) => {
        if (row.isUserLocation) return !query || normalize(this.userLocationLabel).includes(query);
        return normalize(row.entry!.title).includes(query);
      })
      .slice(0, MAX_RESULTS);
    this.renderResults(rows);
  };

  private updateClearButtonVisibility(): void {
    this.clearButton.style.display = this.input.value ? "flex" : "none";
  }

  /** Bouton "x" (voir constructeur) : vide le champ ET la sélection courante — contrairement à
   * `SearchBox.clearButton`, qui n'a qu'une recherche textuelle à effacer, ce champ porte aussi une
   * `RoutePanelSelection` qu'il faut retirer pour que `RoutePanel.handleFieldChange` redésactive le
   * bouton Valider en conséquence (voir `this.onChange()` ci-dessous). */
  private clear(): void {
    this.selection = null;
    this.input.value = "";
    this.updateClearButtonVisibility();
    this.renderResults([]);
    this.input.focus();
    this.onChange();
  }

  private handleOutsideClick = (event: MouseEvent): void => {
    if (event.target instanceof Node && this.wrapper.contains(event.target)) return;
    this.renderResults([]);
  };

  /** Même navigation clavier que `SearchBox.handleKeyDown` (Flèches haut/bas déplacent le résultat
   * surligné, voir `setActiveIndex` ; Entrée sélectionne le résultat actuellement surligné — `0` par
   * défaut dès l'affichage de la liste, voir `renderResults` — pour pouvoir taper puis valider sans
   * toucher la souris). Contrairement à `SearchBox`, Entrée appelle aussi TOUJOURS `onEnter` ensuite
   * (même sans résultat affiché, ex. les deux champs sont déjà remplis et l'utilisateur appuie
   * Entrée dans le second sans y retaper) — c'est ce qui permet à `RoutePanel` de tenter une
   * validation immédiate de l'itinéraire, un concept que `SearchBox` n'a pas. */
  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") {
      this.renderResults([]);
      return;
    }
    if (event.key === "ArrowDown") {
      if (!this.currentRows.length) return;
      event.preventDefault();
      this.setActiveIndex((this.activeIndex + 1) % this.currentRows.length);
      return;
    }
    if (event.key === "ArrowUp") {
      if (!this.currentRows.length) return;
      event.preventDefault();
      this.setActiveIndex((this.activeIndex - 1 + this.currentRows.length) % this.currentRows.length);
      return;
    }
    if (event.key !== "Enter") return;
    event.preventDefault();
    const active = this.activeIndex >= 0 ? this.currentRows[this.activeIndex] : undefined;
    if (active) this.selectRow(active);
    this.onEnter();
  };

  /** Même mécanisme que `SearchBox.setActiveIndex` : bascule la classe `--active` (voir style.css)
   * sur la ligne concernée et la garde visible dans la liste défilante. */
  private setActiveIndex(index: number): void {
    this.activeIndex = index;
    const rows = this.resultsEl.querySelectorAll<HTMLButtonElement>(".toka-route-panel__result");
    rows.forEach((row, rowIndex) => {
      row.classList.toggle("toka-route-panel__result--active", rowIndex === index);
    });
    rows[index]?.scrollIntoView({ block: "nearest" });
  }

  private selectRow(row: FieldRow): void {
    if (row.isUserLocation) {
      this.selection = { type: "user-location" };
      this.input.value = this.userLocationLabel;
    } else if (row.entry!.external) {
      this.selection = { type: "external", payload: row.entry!.external.payload };
      this.input.value = row.entry!.title;
    } else {
      this.selection = { type: "shape", shapeId: row.entry!.shapeIds[0]! };
      this.input.value = row.entry!.title;
    }
    this.updateClearButtonVisibility();
    this.renderResults([]);
    this.onChange();
  }

  private renderResults(rows: FieldRow[]): void {
    this.currentRows = rows;
    this.activeIndex = -1;
    this.resultsEl.innerHTML = "";
    if (!rows.length) {
      this.resultsEl.style.display = "none";
      return;
    }
    this.resultsEl.style.display = "flex";
    for (const row of rows) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "toka-route-panel__result";

      // Même structure que `SearchBox.renderResults` (image ou placeholder + titre/sous-titre) —
      // l'entrée "position utilisateur" (sans `entry`, voir `FieldRow`) affiche un pin à la place
      // d'une vraie image, plutôt qu'un placeholder vide indiscernable d'une entité sans image.
      if (row.isUserLocation) {
        const pin = document.createElement("span");
        pin.className = "toka-route-panel__result-image toka-route-panel__result-image--pin";
        pin.textContent = "📍";
        button.appendChild(pin);
      } else if (row.entry!.image) {
        const img = document.createElement("img");
        img.className = "toka-route-panel__result-image";
        img.src = row.entry!.image;
        img.alt = "";
        button.appendChild(img);
      } else {
        const placeholder = document.createElement("span");
        placeholder.className = "toka-route-panel__result-image toka-route-panel__result-image--placeholder";
        button.appendChild(placeholder);
      }

      const text = document.createElement("span");
      text.className = "toka-route-panel__result-text";

      const title = document.createElement("span");
      title.className = "toka-route-panel__result-title";
      title.textContent = row.isUserLocation ? this.userLocationLabel : row.entry!.title;
      text.appendChild(title);

      const subTitle = row.entry?.subTitle;
      if (subTitle) {
        const subTitleEl = document.createElement("span");
        subTitleEl.className = "toka-route-panel__result-subtitle";
        subTitleEl.textContent = subTitle;
        text.appendChild(subTitleEl);
      }
      button.appendChild(text);

      // `mousedown` (pas `click`) : se déclenche avant le `blur` de l'input qui fermerait sinon la
      // liste avant que le clic n'atteigne cette ligne — même raison que `ui/SearchBox.ts`.
      button.addEventListener("mousedown", (event) => {
        event.preventDefault();
        this.selectRow(row);
      });
      this.resultsEl.appendChild(button);
    }
    // Surligne le premier résultat par défaut (même convention que `SearchBox.renderResults`) : sans
    // ça, Entrée n'aurait rien à sélectionner tant que l'utilisateur n'a pas touché aux flèches.
    this.setActiveIndex(0);
  }

  closeResults(): void {
    this.renderResults([]);
  }

  destroy(): void {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  }
}

/**
 * Panneau "itinéraire" (BESOINS.md n'en parle pas — module ajouté après coup, voir CLAUDE.md
 * "Itinéraire / pathfinding") : 2 champs Départ/Arrivée (voir `RouteField`) + bouton Valider —
 * affiché/masqué par `TokaMapInstance.startRouteMode`/`cancelRouteMode`, jamais construit/détruit à
 * chaque ouverture (même principe que `ui/EntityPanel`, une seule instance pour toute la durée de vie
 * de la carte).
 */
export class RoutePanel {
  private container: HTMLDivElement;
  private fromField: RouteField;
  private toField: RouteField;
  private validateButton: HTMLButtonElement;
  private errorEl: HTMLDivElement;
  private visible = false;

  constructor(
    parent: HTMLElement,
    private options: RoutePanelOptions,
    private onValidate: (from: RoutePanelSelection, to: RoutePanelSelection) => void,
    private onCancel: () => void,
  ) {
    const userLocationLabel = options.userLocationLabel ?? "Ma position";

    this.container = document.createElement("div");
    this.container.className = "toka-route-panel";
    this.container.style.display = "none";
    Object.assign(this.container.style, POSITION_OFFSETS[options.position ?? "top-right"], options.style);

    const header = document.createElement("div");
    header.className = "toka-route-panel__header";
    const title = document.createElement("span");
    title.className = "toka-route-panel__title";
    title.textContent = "Itinéraire";
    header.appendChild(title);
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "toka-route-panel__close";
    closeButton.textContent = "×";
    closeButton.setAttribute("aria-label", "Fermer");
    closeButton.addEventListener("click", () => this.onCancel());
    header.appendChild(closeButton);
    this.container.appendChild(header);

    // Dédoublonne par titre normalisé (voir `normalize`) et trie alphabétiquement : contrairement à
    // `createTokaMap.ts`'s `buildSearchEntries` (dédoublonnée par `SearchItem.key`, qui peut être
    // absent — voir son commentaire, repli sur `shape.id`), plusieurs formes de la même entité sans
    // `key` explicite reviennent ici en entrées distinctes mais avec un titre identique ; les
    // fusionner ici (plutôt que dans `buildSearchEntries`, qui reste utilisé aussi par `SearchBox`,
    // sans qu'on lui impose ce même choix) évite de lister deux fois la même entité dans ce panneau
    // spécifiquement.
    const getShapeRows = (): FieldRow[] => {
      const byTitle = new Map<string, SearchBoxEntry>();
      for (const entry of options.getEntries()) {
        const key = normalize(entry.title);
        const existing = byTitle.get(key);
        if (existing) {
          existing.shapeIds.push(...entry.shapeIds);
          continue;
        }
        byTitle.set(key, { ...entry, shapeIds: [...entry.shapeIds] });
      }
      return [...byTitle.values()]
        .sort((a, b) => a.title.localeCompare(b.title, "fr", { sensitivity: "base" }))
        .map((entry) => ({ isUserLocation: false, entry }));
    };

    // Retire de la liste la forme déjà choisie dans l'AUTRE champ — une entité sélectionnée d'un
    // côté n'a plus de sens comme choix de l'autre (un itinéraire d'un point vers lui-même). Filtre
    // par `shapeIds` (pas juste `shapeIds[0]`) pour couvrir le cas d'une entité découpée en
    // plusieurs formes (voir `SearchItem.key`) même si `RoutePanelSelection.shapeId` n'en retient
    // qu'une seule (`selectRow`).
    const excludeOtherSelection = (rows: FieldRow[], otherSelection: RoutePanelSelection | null): FieldRow[] => {
      if (!otherSelection || otherSelection.type !== "shape") return rows;
      return rows.filter((row) => row.isUserLocation || !row.entry!.shapeIds.includes(otherSelection.shapeId));
    };

    this.fromField = new RouteField(
      () =>
        excludeOtherSelection(
          [...(options.hasUserLocation() ? [{ isUserLocation: true, entry: null }] : []), ...getShapeRows()],
          this.toField?.getSelection() ?? null,
        ),
      options.fromLabel ?? "Départ",
      options.fromPlaceholder ?? "Départ…",
      userLocationLabel,
      () => this.handleFieldChange(),
      () => this.tryValidate(),
    );
    this.toField = new RouteField(
      () => excludeOtherSelection(getShapeRows(), this.fromField.getSelection()),
      options.toLabel ?? "Arrivée",
      options.toPlaceholder ?? "Arrivée…",
      userLocationLabel,
      () => this.handleFieldChange(),
      () => this.tryValidate(),
    );

    this.container.appendChild(this.fromField.wrapper);
    this.container.appendChild(this.toField.wrapper);

    this.errorEl = document.createElement("div");
    this.errorEl.className = "toka-route-panel__error";
    this.errorEl.style.display = "none";
    this.container.appendChild(this.errorEl);

    this.validateButton = document.createElement("button");
    this.validateButton.type = "button";
    this.validateButton.className = "toka-route-panel__validate";
    this.validateButton.textContent = options.validateLabel ?? "Valider";
    this.validateButton.disabled = true;
    this.validateButton.addEventListener("click", () => {
      const from = this.fromField.getSelection();
      const to = this.toField.getSelection();
      if (from && to) this.onValidate(from, to);
    });
    this.container.appendChild(this.validateButton);

    parent.appendChild(this.container);
  }

  /** Appelée sur Entrée dans l'un des 2 champs (voir `RouteField.onEnter`) — revalide seulement si
   * les deux champs ont maintenant une sélection valide, même condition que le bouton Valider
   * (`validateButton.disabled`, tenu à jour par `handleFieldChange`), pour ne pas dupliquer cette
   * logique ici. */
  private tryValidate(): void {
    if (this.validateButton.disabled) return;
    const from = this.fromField.getSelection();
    const to = this.toField.getSelection();
    if (from && to) this.onValidate(from, to);
  }

  private handleFieldChange(): void {
    this.setError(null);
    const from = this.fromField.getSelection();
    const to = this.toField.getSelection();
    // Une position/forme identique des deux côtés n'a pas de sens (voir `core/pathfinding.ts`, qui
    // gère pourtant ce cas pour un usage direct hors panneau — ici on préfère désactiver Valider
    // plutôt que de renvoyer un itinéraire trivial d'un seul point).
    const sameShape = from?.type === "shape" && to?.type === "shape" && from.shapeId === to.shapeId;
    this.validateButton.disabled = !from || !to || sameShape;
  }

  /**
   * Affiche le panneau — `prefill.from`/`prefill.to` reprennent `StartRouteModeOptions` (voir
   * `TokaMapInstance.startRouteMode`). Sans `prefill.from` explicite, le champ Départ se préremplit
   * automatiquement sur la position utilisateur si `options.hasUserLocation()` répond vrai à cet
   * instant (voir CLAUDE.md "Itinéraire / pathfinding") — sinon il reste vide.
   */
  show(prefill?: { from?: RoutePanelSelection; to?: { shapeId: string } }): void {
    this.container.style.display = "flex";
    this.visible = true;
    this.setError(null);

    const from = prefill?.from ?? (this.options.hasUserLocation() ? { type: "user-location" as const } : null);
    this.fromField.setSelection(from);
    this.toField.setSelection(prefill?.to ? { type: "shape", shapeId: prefill.to.shapeId } : null);
    this.handleFieldChange();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.container.style.display = "none";
    this.fromField.closeResults();
    this.toField.closeResults();
  }

  isVisible(): boolean {
    return this.visible;
  }

  /** Sélections courantes des 2 champs — sert à `TokaMapInstance.startRouteMode`'s
   * `autoValidate` (voir `StartRouteModeOptions`) pour valider tout de suite après `show()`, sans
   * dupliquer ici la logique de repli sur la position utilisateur qu'il vient d'appliquer. */
  getSelections(): { from: RoutePanelSelection | null; to: RoutePanelSelection | null } {
    return { from: this.fromField.getSelection(), to: this.toField.getSelection() };
  }

  /** Message affiché sous les champs (ex. "Aucun itinéraire trouvé.") — `null` l'efface. Le panneau
   * reste ouvert : à l'utilisateur de changer un des deux champs et revalider. */
  setError(message: string | null): void {
    this.errorEl.textContent = message ?? "";
    this.errorEl.style.display = message ? "block" : "none";
  }

  destroy(): void {
    this.fromField.destroy();
    this.toField.destroy();
    this.container.remove();
  }
}
